import Farm from "@/farm/Farm"
import ValueRange from "@/effects/ValueRange"
import { object, serializable, serialize } from "serializr"
import ValueMod from "@/effects/ValueMod"

export default class Market {
  // The amount of demand (L) of the product
  @serializable(object(ValueRange))
  public demand: ValueRange = new ValueRange(5, 0, 10, [], true)

  @serializable(object(ValueRange))
  public demandMax: ValueRange = new ValueRange(0, 0, undefined, [], true)

  // Contributes to market width and demand generation
  @serializable(object(ValueRange))
  public consumption: ValueRange = new ValueRange(0.2, 0, 1000, [], true)

  // Represents rival producers. Contributes to market width.
  @serializable(object(ValueRange))
  public competition: ValueRange = new ValueRange(1, 0, 1000, [], true)

  // The lowest possible price of the product ($ / L)
  @serializable(object(ValueRange))
  public basePrice: ValueRange = new ValueRange(30, 30, 1000, [], true)

  // How much the price rises per market width. ($ / L)
  @serializable(object(ValueRange))
  public marketIntensity: ValueRange = new ValueRange(2, 1, 1000, [], true)

  @serializable(object(ValueRange))
  public sellCooldown: ValueRange = new ValueRange(0, 0, 60, [], true)

  private farm: Farm

  // @ts-ignore
  constructor(farm: Farm = undefined) {
    this.farm = farm
  }

  load(state: any) {
    this.demand.load(state.demand)
    this.consumption.load(state.consumption, false)
    this.competition.load(state.competition)
    this.basePrice.load(state.basePrice)
    this.marketIntensity.load(state.marketIntensity)
  }

  update(dt: number) {
    this.demand.addBaseValue(this.demandGeneration * dt)
    this.sellCooldown.addBaseValue(-dt)

    if (this.demand.fraction == 1) {
      this.competition.addBaseValue(1)
    }

    this.demand.maxMods["marketWidth"] = new ValueMod(Math.pow(1.5, this.marketWidth - 1), 'mul')
    this.marketIntensity.valueMods["marketWidth"] = new ValueMod(1 * (this.marketWidth - 1), 'add')
  }

  get demandGeneration(): number {
    return (this.consumption.value * Math.max(0.3, 1 - this.demand.fraction)) / this.marketWidth
  }

  /**
   * How much product is needed before the market becomes willing to pay a higher price. (L)
   */
  get marketWidth(): number {
    return this.competition.value
  }

  get marketDepth(): number {
    let layers = this.demand.value / this.marketWidth
    let fullLayers = Math.floor(layers)
    return fullLayers
  }

  /** How much product in the given layer costs ($ / L) */
  private layerPrice(layer: number): number {
    return this.basePrice.value + layer * this.marketIntensity.value
  }

  predictSaleAmount(price: number): number {
    let layers = this.demand.value / this.marketWidth
    let fullLayers = Math.floor(layers)
    let topLayerAmount = (layers - fullLayers) * this.marketWidth

    let saleAmount = 0
    if (this.layerPrice(fullLayers + 1) >= price) saleAmount += topLayerAmount
    for (let layer = fullLayers; layer >= 0; layer--) {
      if (this.layerPrice(layer) >= price) saleAmount += this.marketWidth
    }

    return saleAmount
  }

  /**
   * Sells an amount of goods. Returns how much was sold, and how much money was sold for.
   *
   * @param amount How much to try to sell
   * @param price At which price to sell
   */
  sell(amount: number, price: number): {sold: number; money: number} {
    if (this.sellCooldown.fraction > 0) return { sold: 0, money: 0 }
    this.sellCooldown.addBaseValue(1000)

    let amountSold = Math.min(amount, this.predictSaleAmount(price))
    let totalPrice = amountSold * price
    this.demand.addBaseValue(-amountSold)
    return { sold: amountSold, money: totalPrice }
  }

  get maxPrice(): number {
    let layers = this.demand.value / this.marketWidth
    let fullLayers = Math.floor(layers)
    return this.layerPrice(fullLayers + 1)
  }

  toJSON() {
    return serialize(this)
  }
}
